import { useEffect, useState } from "react";

/**
 * Animated, deterministic dashboard mockup for the landing-page hero.
 * Uses inline SVG sparkline + pulsing "live" dots + a faux Zi-score gauge
 * that ticks up on mount. No backend calls — pure visual proof-of-product.
 */
export default function HeroMockup() {
  const [zi, setZi] = useState(0);
  const [pulseIdx, setPulseIdx] = useState(0);

  useEffect(() => {
    let v = 0;
    const target = 86;
    const iv = setInterval(() => {
      v += 2;
      if (v >= target) { v = target; clearInterval(iv); }
      setZi(v);
    }, 22);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const iv = setInterval(() => setPulseIdx(i => (i + 1) % 5), 1200);
    return () => clearInterval(iv);
  }, []);

  // Equity curve — deterministic, looks like an upward staircase
  const points = [10000, 10080, 10040, 10220, 10310, 10470, 10540, 10720, 10810, 11020, 11180, 11380];
  const min = Math.min(...points), max = Math.max(...points);
  const w = 280, h = 88, padY = 6;
  const pts = points.map((v, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = padY + (1 - (v - min) / (max - min)) * (h - padY * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <div data-testid="hero-mockup" style={{
      position: "relative",
      width: "100%", maxWidth: 460, margin: "0 auto",
      borderRadius: 22, padding: 18,
      background: "linear-gradient(165deg, rgba(167,139,250,0.08), rgba(96,165,250,0.04))",
      border: "1px solid rgba(255,255,255,0.08)",
      backdropFilter: "blur(16px)",
      boxShadow: "0 25px 80px rgba(2,6,23,0.6), 0 0 0 1px rgba(255,255,255,0.02) inset",
      transform: "perspective(1200px) rotateX(2deg) rotateY(-1.5deg)",
    }}>
      {/* OS-window bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14 }}>
        {["#ef4444", "#f59e0b", "#22c55e"].map(c => (
          <div key={c} style={{ width: 10, height: 10, borderRadius: 5, background: c, opacity: 0.7 }} />
        ))}
        <div style={{ marginLeft: 10, fontSize: 9, color: "#8b949e", letterSpacing: 1.5 }}>OMEGA · COMMAND DASHBOARD</div>
        <div style={{ marginLeft: "auto", fontSize: 9, color: "#4ade80", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 6, height: 6, borderRadius: 3, background: "#4ade80", animation: "pulse 1.5s infinite" }} />
          LIVE
        </div>
      </div>

      {/* Top stats strip */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 10, marginBottom: 12 }}>
        <div style={{ padding: 12, borderRadius: 12, background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)" }}>
          <div style={{ fontSize: 9, color: "#4ade80", letterSpacing: 1.5, fontWeight: 700 }}>WEEKLY P&L</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#4ade80", letterSpacing: -0.5 }}>+$3,284</div>
          <div style={{ fontSize: 10, color: "#86efac" }}>+12.4% · 14 trades</div>
        </div>
        <div style={{ padding: 12, borderRadius: 12, background: "rgba(96,165,250,0.06)", border: "1px solid rgba(96,165,250,0.2)" }}>
          <div style={{ fontSize: 9, color: "#60a5fa", letterSpacing: 1.5, fontWeight: 700 }}>ZI SCORE</div>
          <div data-testid="hero-zi" style={{ fontSize: 22, fontWeight: 800, color: "#60a5fa", letterSpacing: -0.5, fontVariantNumeric: "tabular-nums" }}>{zi}</div>
          <div style={{ fontSize: 10, color: "#93c5fd" }}>NVDA · STRONG</div>
        </div>
      </div>

      {/* Sparkline */}
      <div style={{ borderRadius: 12, background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.04)", padding: "10px 12px", marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 9, color: "#8b949e", letterSpacing: 1.5 }}>EQUITY · LAST 30D</div>
          <div style={{ fontSize: 10, color: "#4ade80" }}>+13.8%</div>
        </div>
        <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
          <defs>
            <linearGradient id="heroGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#4ade80" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#4ade80" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon points={`0,${h} ${pts} ${w},${h}`} fill="url(#heroGrad)" />
          <polyline points={pts} fill="none" stroke="#4ade80" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
      </div>

      {/* Engine pulse strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
        {[
          { n: "MC", c: "#60a5fa" },
          { n: "Ψ", c: "#a78bfa" },
          { n: "◉", c: "#4ade80" },
          { n: "✂", c: "#fbbf24" },
          { n: "↻", c: "#22d3ee" },
        ].map((e, i) => (
          <div key={i} style={{
            padding: "10px 4px", borderRadius: 9, textAlign: "center",
            background: `${e.c}10`, border: `1px solid ${e.c}33`,
            color: e.c, fontSize: 14, fontWeight: 700,
            boxShadow: pulseIdx === i ? `0 0 12px ${e.c}88` : "none",
            transform: pulseIdx === i ? "scale(1.06)" : "scale(1)",
            transition: "transform 320ms ease, box-shadow 320ms ease",
          }}>{e.n}</div>
        ))}
      </div>
    </div>
  );
}
